import React, {ReactElement, useState} from "react";
import {
  IKensuCalendarEventProto,
  IMutateCalendarEventRequest,
  MutateCalendarEventRequest
} from "../../../../provider_api";
import './Calendar.css';
import {EventModal} from "./EventModal";
import {useMutateCalendarEvent} from "../../../../util/RpcHooks/MutateCalendarEvent";
import {ConfirmEditPrompt} from "./ConfirmEditPrompt";
import {getEventType} from "./CalendarUtil";

type Props = {

  // EventInstanceProto2 is a denormalized, flattened version of the event
  // and event instance that is much easier to deal with.
  existingEvent: IKensuCalendarEventProto

  // Hack hack. The servers sends down classes and class bookings separately, so if this
  // editor is for a class, we put bookings in here.
  classBookings: IKensuCalendarEventProto[],

  // The class editor can remove people from bookings...
  onClassBookingDeleted: (bookingId: string) => void,

  // The range of the current calendar view, this is used to request all
  // the instances of the newly created event. 
  calendarViewStart: Date,
  calendarViewEnd: Date,

  // If this is an update for a single event, it returns either the event (in case of update)
  // or an empty list (in case of delete). The client should replace the existing event
  // with the one in the list in either case.
  // 
  // If it was for this and future events, the client should clear all existing events in the
  // existing series first, then add all returned events to the calendar.
  onUpdated: (
      existingEvent: IKensuCalendarEventProto,
      events: IKensuCalendarEventProto[], 
      includeFutureEvents: boolean) => void,
  onCancel: () => void,
}

export const EditEventModal = (props: Props) => {

  const mutateRpc = useMutateCalendarEvent();

  /**
   * This is the only overlay with an overlay. When editing or deleting events, we have
   * to ask whether they want to apply it to all future instances too. This is a
   * ConfirmEditPrompt.
   */
  const [overlay, setOverlay] = useState<ReactElement>();

  const bookingString = (n: number)=> {
    return n + (n > 1 ? " bookings" : " booking");
  }
  
  /**
   * If the event is a non-recurring instance, sends the RPC immediately. Otherwise
   * opens a prompt to check whether the user wants to update this instance only or
   * all future instances.
   */
  const handleUpdateOrDelete = (instance: IKensuCalendarEventProto, isDelete: boolean) => {

    const request = new MutateCalendarEventRequest({
      event: instance,
      clientStartTimestampMs: props.calendarViewStart.getTime(),
      clientEndTimestampMs: props.calendarViewEnd.getTime(),
    });

    if (isDelete && props.existingEvent.appointment &&
        !window.confirm("Are you sure you want to delete this appointment? The client " +
            "will be refunded if they've already paid.")) {
      return;
    }
    
    if (isDelete && props.existingEvent.class && props.classBookings.length > 0 &&
        !window.confirm("This class has " + bookingString(props.classBookings.length) +
            ". Are you sure you want to delete this class? All clients will be refunded the " +
            "class credit or package they paid with.")) {
      return;
    }

    // If a recurring event is being edited we prompt to see if they want just this event
    // changed, or this and future events
    const isRecurringEvent =
        props.existingEvent.class?.repeatStrategy?.weekly ||
        props.existingEvent.availability?.repeatStrategy?.weekly;

    if (!isRecurringEvent) {
      sendRpc(request, isDelete);
      return;
    }

    setOverlay(<ConfirmEditPrompt
        isDelete={isDelete}
        onConfirm={(updateFutureEvents) => {
          sendRpc(new MutateCalendarEventRequest({
            ...request,
            includeFutureInstances: updateFutureEvents
          }), isDelete);
          setOverlay(undefined);
        }}
        onCancel={() => setOverlay(undefined)}
    />);
    return;
  }

  const sendRpc = (request: IMutateCalendarEventRequest, isDelete: boolean) => {

    const method = (isDelete ? 'delete_' : 'update_') + getEventType(request.event as IKensuCalendarEventProto);

    mutateRpc.send({
      method: method,
      request: request,
      onSuccess: (instances) => {
        setOverlay(undefined)
        props.onUpdated(request.event as IKensuCalendarEventProto,
            instances,
            request.includeFutureInstances || false);
      },

      // If there were errors, keep the edit modal open but get rid of the
      // "this and future instances" overlay
      onError: () => setOverlay(undefined)
    });
  }

  return <>
    <EventModal
        instance={props.existingEvent}
        classBookings={props.classBookings}
        onClassBookingDeleted={props.onClassBookingDeleted}
        mutateRpcState={mutateRpc.rpcState}
        errors={mutateRpc.errors}
        mode={'update'}
        onSave={event => handleUpdateOrDelete(event, false)}
        onDelete={() => handleUpdateOrDelete(props.existingEvent, true)}
        onCancel={props.onCancel}
    />
    {overlay}
  </>
};
